import {
  TextField,
  DateField,
  FunctionField,
  NumberField,
  TopToolbar,
  CreateButton,
  SelectColumnsButton,
  ExportButton,
  ReferenceField,
  ListContextProvider,
  useListController,
  defaultExporter,
  Exporter,
} from "react-admin";
import { displayContractBasis, displayContractBasisForExport } from "@/utils/contractBasis";
import { ChunkedExportButton } from "@/components/common/ChunkedExportButton";
import { PolicyListAside } from "./components/PolicyListAside";
import { getProps } from "@/utils/helpers/columnsHelper";
import { StatusChipField } from "@/components/common/fields/StatusChipField";
import List from "@/components/ui/react-admin/styledList/StyledList";
import { StyledDatagridConfigurable } from "@/components/ui/react-admin/StyledDatagrid";
import { PageHeader } from "@/components/common/PageHeader";
import { getResourceIcon } from "@/utils/icons";
import { TruncatedFieldWrapper } from "@/components/common/fields/TruncatedFieldWrapper";
import { ResourceName } from "@/types";
import { defaultCurrencyFormatterOptions } from "@/utils/formatters/currencyFormatter";
import { useResourcePermissions } from "@/hooks/useResourcePermissions";
import { ListEmptyState } from "@/components/common/ListEmptyState";
import { jsonExporter } from "@/utils/exporters/jsonExporter";
import { isNonProductionEnvironment } from "@/utils/environment";
import { Download as DownloadIcon } from "@mui/icons-material";

// CSV export goes through the same display rule as the grid: "Paid / 12", never 999
// (#7320 AC2, !4843 thread 29877). Nested benefit bases included.
const policyListExporter: Exporter = (records, fetchRelatedRecords, dataProvider, resource) =>
  defaultExporter(records.map(displayContractBasisForExport), fetchRelatedRecords, dataProvider, resource);

const PolicyListActions = ({ loading }: { loading?: boolean }) => {
  const { canCreate } = useResourcePermissions(ResourceName.POLICIES);

  return (
    <TopToolbar>
      {canCreate && <CreateButton disabled={loading} />}
      <SelectColumnsButton disabled={loading} />
      <ChunkedExportButton disabled={loading} exporter={policyListExporter} />
      {isNonProductionEnvironment() && (
        <ExportButton
          label="Export JSON"
          icon={<DownloadIcon />}
          exporter={(records) => jsonExporter(records, ResourceName.POLICIES)}
          disabled={loading}
        />
      )}
    </TopToolbar>
  );
};

export const PolicyList = () => {
  // Explicit default sort preserves the prior server order (effective_start_date
  // newest first) now that the API honors sort. Without it react-admin would
  // default to id ASC.
  const listContext = useListController({
    resource: ResourceName.POLICIES,
    sort: { field: "effective_start_date", order: "DESC" },
  });

  const { isPending: loading } = listContext;

  const PolicyIcon = getResourceIcon(ResourceName.POLICIES);

  return (
    <ListContextProvider value={listContext}>
      <PageHeader
        customTitle="Policies"
        hideSectionTitle
        rightContent={<PolicyListActions loading={loading} />}
        headerIcon={<PolicyIcon sx={{ fontSize: 28, color: "primary.main" }} />}
      />
      <List
        perPage={25}
        sort={{ field: "effective_start_date", order: "DESC" }}
        aside={<PolicyListAside />}
        actions={false}
        loading={loading}
        empty={
          <ListEmptyState resource={ResourceName.POLICIES} subtitle="Adjust your filters or create a new policy" />
        }
      >
        <StyledDatagridConfigurable
          rowClick="show"
          sx={{
            "& .column-groups": {
              md: { display: "none" },
              lg: { display: "table-cell" },
            },
          }}
        >
          <TruncatedFieldWrapper {...getProps("aegle_id", ResourceName.POLICIES)}>
            <TextField source="aegle_id" />
          </TruncatedFieldWrapper>
          <StatusChipField {...getProps("status", ResourceName.POLICIES)} />
          <ReferenceField
            {...getProps("vendor_id_ref", ResourceName.POLICIES)}
            reference={ResourceName.VENDORS}
            sortable={false}
          >
            <TextField source="company_name" />
          </ReferenceField>
          <ReferenceField
            {...getProps("client_id_ref", ResourceName.POLICIES)}
            reference={ResourceName.CLIENTS}
            sortable={false}
          >
            <TextField source="client_legal_name" />
          </ReferenceField>
          <DateField {...getProps("effective_start_date", ResourceName.POLICIES)} textAlign="right" />
          <DateField {...getProps("effective_end_date", ResourceName.POLICIES)} textAlign="right" />
          <FunctionField
            {...getProps("contract_basis", ResourceName.POLICIES)}
            render={(record: { contract_basis?: string | null }) => displayContractBasis(record.contract_basis)}
          />
          <NumberField
            {...getProps("specific_benefit.specific_deductible", ResourceName.POLICIES)}
            options={defaultCurrencyFormatterOptions}
            sortable={false}
          />
          <NumberField
            {...getProps("aggregate_benefit.aggregate_deductible", ResourceName.POLICIES)}
            options={defaultCurrencyFormatterOptions}
            sortable={false}
          />
        </StyledDatagridConfigurable>
      </List>
    </ListContextProvider>
  );
};
